import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { CoachingRealtimeService } from '../../services/coaching-realtime.service';
import {
  CoachingService,
  InboxMode,
  InboxThread,
  productLineLabel
} from '../../services/coaching.service';
import { initials, isStaff } from '../../services/chat-utils';

const REFRESH_MS = 60000;
const LIVE_REFRESH_DEBOUNCE_MS = 1500;
const MOBILE_BP = 768;

/**
 * Global unread coaching conversations for admins / trainers.
 * Desktop: right dock, collapsed to a strip by default. Mobile: FAB + panel.
 * Totals come from CoachingRealtimeService's unread store; this only lists rows.
 * Hidden on the admin Chats tab, where the inbox already shows the same list.
 */
@Component({
  selector: 'app-coaching-unread-dock',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './coaching-unread-dock.component.html',
  styleUrls: ['./coaching-unread-dock.component.scss']
})
export class CoachingUnreadDockComponent implements OnInit, OnDestroy {
  threads: InboxThread[] = [];
  unreadTotal = 0;
  loading = false;
  collapsed = true;
  byThread: Readonly<Record<string, number>> = {};
  mobileOpen = false;
  isMobile = false;
  hideForRoute = false;
  isCoachViewer = false;
  mode: InboxMode = 'admin';

  private destroy$ = new Subject<void>();
  private liveRefresh$ = new Subject<void>();
  readonly productLineLabel = productLineLabel;
  readonly initials = initials;

  constructor(
    private auth: AuthService,
    private coaching: CoachingService,
    private realtime: CoachingRealtimeService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.updateViewport();

    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.resolveViewer();
      if (user && this.isCoachViewer) {
        this.refresh();
      } else {
        this.threads = [];
        this.unreadTotal = 0;
      }
    });

    this.realtime.messages$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (!this.isCoachViewer || this.hideForRoute) {
        return;
      }
      if (event.message.from_user) {
        this.liveRefresh$.next();
      }
    });
    this.liveRefresh$.pipe(debounceTime(LIVE_REFRESH_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => this.refresh(true));
    this.realtime.resync$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.hideForRoute) {
        this.refresh(true);
      }
    });

    this.realtime.unread$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.unreadTotal = state.coachTotal;
      this.byThread = state.byThread;
    });

    interval(REFRESH_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isCoachViewer && !document.hidden && !this.hideForRoute) {
        this.refresh(true);
      }
    });

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(e => this.updateRouteVisibility(e.urlAfterRedirects));
    this.updateRouteVisibility(this.router.url);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  updateViewport(): void {
    this.isMobile = window.innerWidth < MOBILE_BP;
  }

  get showShell(): boolean {
    return this.isCoachViewer && !this.hideForRoute;
  }

  get showFab(): boolean {
    return this.showShell && this.isMobile;
  }

  get showStrip(): boolean {
    return this.showShell && !this.isMobile && this.collapsed;
  }

  get showPanel(): boolean {
    if (!this.showShell) {
      return false;
    }
    if (this.isMobile) {
      return this.mobileOpen;
    }
    return !this.collapsed;
  }

  toggle(): void {
    if (this.isMobile) {
      this.mobileOpen = !this.mobileOpen;
      if (this.mobileOpen) {
        this.refresh();
      }
      return;
    }
    this.collapsed = !this.collapsed;
    if (!this.collapsed) {
      this.refresh();
    }
  }

  close(): void {
    if (this.isMobile) {
      this.mobileOpen = false;
      return;
    }
    this.collapsed = true;
  }

  refresh(silent = false): void {
    if (!this.isCoachViewer) {
      return;
    }
    if (!silent) {
      this.loading = true;
    }
    this.coaching.listThreads(this.mode, { scope: 'mine', unread: true, page: 1, limit: 40 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.threads = res.items;
          this.realtime.ingestInboxThreads(res.items);
          this.loading = false;
        },
        error: () => { this.loading = false; }
      });
  }

  openThread(threadId: string): void {
    const path = this.mode === 'admin' ? '/manage/coaching' : '/coach-console/inbox';
    this.router.navigate([path], { queryParams: { thread: threadId, tab: this.mode === 'admin' ? 'chats' : undefined } });
    if (this.isMobile) {
      this.mobileOpen = false;
    }
  }

  /** Rows still unread according to the store (a row read elsewhere drops out immediately). */
  get visibleThreads(): InboxThread[] {
    return this.threads.filter(t => (this.byThread[t.thread_id] ?? t.unread_count) > 0);
  }

  unreadFor(t: InboxThread): number {
    return this.byThread[t.thread_id] ?? t.unread_count ?? 0;
  }

  trackById(_: number, item: InboxThread): string {
    return item.thread_id;
  }

  private resolveViewer(): void {
    const roles = this.auth.getRoles().map(r => String(r).toUpperCase());
    this.isCoachViewer = isStaff(roles);
    this.mode = roles.includes('ADMIN') ? 'admin' : 'coach';
  }

  private updateRouteVisibility(url: string): void {
    // Global, except auth screens, post-checkout pages (they'd cover the confirmation card)
    // and the admin Chats tab, which already lists every conversation.
    const tree = this.router.parseUrl(url);
    const path = url.split('?')[0].split('#')[0];
    const onChatsTab = path.startsWith('/manage/coaching') && (tree.queryParams['tab'] || 'chats') === 'chats';
    const wasHidden = this.hideForRoute;
    this.hideForRoute = url.startsWith('/auth') || url.startsWith('/subscription') || onChatsTab;
    if (this.hideForRoute) {
      this.mobileOpen = false;
    } else if (wasHidden && this.isCoachViewer) {
      this.refresh(true); // rows may be stale after a stretch on a hidden route
    }
  }
}
